/**
 * =============================================================================
 * IDS CRM Web - Move a work log to another ticket
 * =============================================================================
 *
 * Gareth 2026-09-15: a ticket holding a call-out cannot be deleted until the
 * work is moved to the ticket being kept. This is where that is done, and it
 * opens from two places:
 *  - the ticket's Work Logs list, beside Change crew and Reopen;
 *  - the On Call Payroll rescue card, for call-outs already stranded on a
 *    ticket somebody deleted.
 *
 * WHAT IT PROTECTS
 *  - Check pay effect runs the move as a DRY RUN: nothing is written, and the
 *    answer shows the fee and overtime per engineer per night BEFORE and
 *    AFTER, so a manager sees two £20 fees merging into one before agreeing.
 *  - A different customer is an ADMIN action with an explicit tick, because
 *    the customer's out-of-hours charge moves with the work.
 *  - The reason is mandatory here as well as on the server: this moves someone
 *    else's paid hours, and they are told about it.
 *
 * The API owns every refusal and every wording; this renders what comes back.
 * =============================================================================
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import './MoveLogModal.css';

const MIN_REASON = 5;

const gbp = (value) => `£${Number(value || 0).toFixed(2)}`;

const whenText = (value) => {
  if (!value) return '';
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) return String(value);
  return instant.toLocaleString('en-GB', {
    timeZone: 'Europe/London', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit'
  });
};

/**
 * @param {object} props
 * @param {string} props.ticketId - the ticket the work is on now
 * @param {object} props.log - the work log ({log_id, engineer_name, started_at})
 * @param {string} [props.customerId] - the source ticket's customer, to offer
 *   that customer's tickets first
 * @param {string} [props.ticketRef]
 * @param {object} props.user - the signed-in user; only an admin may move
 *   work across customers
 * @param {Function} props.authFetch
 * @param {Function} props.onClose
 * @param {Function} props.onSaved - called with the API's response
 */
function MoveLogModal({ ticketId, ticketRef, log, customerId, user, authFetch, onClose, onSaved }) {
  const [search, setSearch] = useState('');
  const [sameCustomerOnly, setSameCustomerOnly] = useState(true);
  const [options, setOptions] = useState([]);
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [targetId, setTargetId] = useState('');
  const [reason, setReason] = useState('');
  const [confirmCustomerChange, setConfirmCustomerChange] = useState(false);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const isAdmin = user?.role === 'admin';
  const reasonOk = reason.trim().length >= MIN_REASON;
  const target = useMemo(
    () => options.find(option => option.ticket_id === targetId) || null,
    [options, targetId]
  );
  const customerChanges = Boolean(target && customerId && String(target.customer_id) !== String(customerId));
  const blockedByCustomer = customerChanges && (!isAdmin || !confirmCustomerChange);

  const loadOptions = useCallback(async () => {
    setLoadingOptions(true);
    try {
      const params = new URLSearchParams({ limit: '20' });
      if (search.trim()) params.set('search', search.trim());
      if (sameCustomerOnly && customerId) params.set('customer_id', customerId);
      const response = await authFetch(`/api/v1/tickets?${params}`);
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Could not load tickets');
      setOptions((data.data || []).filter(row => row.ticket_id !== ticketId));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoadingOptions(false);
    }
  }, [authFetch, customerId, sameCustomerOnly, search, ticketId]);

  useEffect(() => { loadOptions(); }, [loadOptions]);

  // A target chosen and then filtered out of the list would otherwise stay
  // selected invisibly, and the [Move] button would promise something the
  // person can no longer see.
  useEffect(() => {
    if (targetId && !options.some(option => option.ticket_id === targetId)) {
      setTargetId('');
      // The figures belong to the ticket that is no longer selected.
      setPreview(null);
      setConfirmCustomerChange(false);
    }
  }, [options, targetId]);

  const submit = async (dryRun) => {
    setBusy(true);
    setError(null);
    try {
      const response = await authFetch(`/api/v1/tickets/${ticketId}/work-log/${log.log_id}/move`, {
        method: 'POST',
        body: JSON.stringify({
          target_ticket_id: targetId,
          reason: reason.trim(),
          dry_run: dryRun,
          confirm_customer_change: confirmCustomerChange
        })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.message || 'Could not move the work log');
      if (dryRun) {
        setPreview(data);
      } else {
        onSaved(data);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const previewRows = preview?.pay_preview?.rows || [];
  // An open or parked call-out pays nothing before OR after, so an empty table
  // would read as "this move does not matter". The API says what is true.
  const payNote = preview?.pay_preview?.note || null;
  const partsMoved = Number(preview?.parts_moved) || 0;

  return (
    <div className="move-log-modal-overlay">
      <div className="move-log-modal" onClick={event => event.stopPropagation()}>
        <div className="move-log-header">
          <h2>Move this work to another ticket</h2>
          <button type="button" className="move-log-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>
        <div className="move-log-body">
          {error && <div className="move-log-error">{error}</div>}
          <div className="move-log-hint">
            {log.engineer_name || 'An engineer'}&rsquo;s work started {whenText(log.started_at)}
            {ticketRef ? ` on ${ticketRef}` : ''}. Everyone who was on this job moves with it.
            Parts used recorded with this work move with it. Parts added separately, assignment
            history and timeline entries stay behind.
          </div>

          <div className="move-log-field">
            <label htmlFor="move_log_search">Move it to</label>
            <input
              id="move_log_search"
              type="text"
              value={search}
              placeholder="Search by ticket reference, subject or customer"
              onChange={event => setSearch(event.target.value)}
            />
            <label className="move-log-help">
              <input
                type="checkbox"
                checked={sameCustomerOnly}
                onChange={event => setSameCustomerOnly(event.target.checked)}
              />
              {' '}Only this customer&rsquo;s tickets
            </label>
            <div className="move-log-targets">
              {loadingOptions && <div className="move-log-target">Loading tickets…</div>}
              {!loadingOptions && options.length === 0 && (
                <div className="move-log-target">No other tickets found.</div>
              )}
              {options.map(option => (
                <label className="move-log-target" key={option.ticket_id}>
                  <input
                    type="radio"
                    name="move_log_target"
                    value={option.ticket_id}
                    checked={targetId === option.ticket_id}
                    // The tick belongs to the ticket it was given for: a
                    // different target is a different customer's bill, so it
                    // has to be agreed again (Gareth Q17).
                    onChange={() => {
                      setTargetId(option.ticket_id);
                      setPreview(null);
                      setConfirmCustomerChange(false);
                    }}
                  />
                  <span>
                    <span className="move-log-target-ref">{option.ticket_ref}</span>
                    <span className="move-log-target-meta">
                      {option.subject}
                      {option.customer_name ? ` · ${option.customer_name}` : ''}
                    </span>
                  </span>
                </label>
              ))}
            </div>
          </div>

          {customerChanges && (
            <div className="move-log-warning">
              {target.customer_name || 'That ticket'} is a different customer. The out-of-hours charge for
              this work moves to them.
              {isAdmin ? (
                <label className="move-log-help">
                  <input
                    type="checkbox"
                    checked={confirmCustomerChange}
                    onChange={event => setConfirmCustomerChange(event.target.checked)}
                  />
                  {' '}I understand the customer charge moves too
                </label>
              ) : (
                <span className="move-log-help">Only an admin can move work between customers.</span>
              )}
            </div>
          )}

          <div className="move-log-field">
            <label htmlFor="move_log_reason">Why is it moving? *</label>
            <textarea
              id="move_log_reason"
              rows={2}
              value={reason}
              onChange={event => setReason(event.target.value)}
              placeholder="e.g. duplicate ticket raised for the same fault"
            />
            <span className="move-log-help">
              Recorded against the work log, and sent to each engineer whose hours move.
            </span>
          </div>

          {preview && (
            <div className="move-log-preview">
              {/* The message already opens with 'Nothing has been saved.' */}
              <strong>{preview.message}</strong>
              {/* Parts used filed with the work go with it (spare parts, Push 2). */}
              {partsMoved > 0 && (
                <div>{`${partsMoved} ${partsMoved === 1 ? 'part used moves' : 'parts used move'} with it.`}</div>
              )}
              {preview.warnings?.map(warning => <div key={warning}>{warning}</div>)}
              <table>
                <thead>
                  <tr>
                    <th>Engineer</th>
                    <th>Night</th>
                    <th>Call-out fee</th>
                    <th>Overtime</th>
                  </tr>
                </thead>
                <tbody>
                  {previewRows.map(row => (
                    <tr key={`${row.engineer_id}-${row.night}`}>
                      <td>{row.engineer_name || 'Engineer'}</td>
                      <td>{row.night_label || row.night}</td>
                      <td>{gbp(row.fee_gbp_before)} → {gbp(row.fee_gbp_after)}</td>
                      <td>{gbp(row.overtime_gbp_before)} → {gbp(row.overtime_gbp_after)}</td>
                    </tr>
                  ))}
                  {previewRows.length === 0 && (
                    <tr><td colSpan="4">{payNote || 'No on-call pay is affected by this move.'}</td></tr>
                  )}
                </tbody>
              </table>
              {payNote && previewRows.length > 0 && <div className="move-log-help">{payNote}</div>}
            </div>
          )}
        </div>
        <div className="move-log-footer">
          <button type="button" className="move-log-btn" onClick={onClose}>Cancel</button>
          <button
            type="button"
            className="move-log-btn"
            onClick={() => submit(true)}
            disabled={busy || !targetId || !reasonOk || blockedByCustomer}
          >
            Check pay effect
          </button>
          <button
            type="button"
            className="move-log-btn primary"
            onClick={() => submit(false)}
            disabled={busy || !targetId || !reasonOk || blockedByCustomer}
          >
            {busy ? 'Working…' : 'Move'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MoveLogModal;
